import { Controller } from "@hotwired/stimulus"

// Drag the right edge of any left rail (events, laps, calendar, future
// rails) to resize it. Mechanic and tokens are shared across rails;
// each rail names its own storage key so widths are remembered per
// surface.
//
//   data-controller="resizable-sidebar"
//   data-resizable-sidebar-storage-key-value="events-sidebar:width"
//   data-resizable-sidebar-default-value="340"
//   data-resizable-sidebar-min-value="240"
//   data-resizable-sidebar-max-value="520"
//
// A right-hand drawer drags its LEFT edge, where moving the pointer left makes
// it wider — the opposite of a left rail. Pass edge="start" for that (and give
// the handle the .sidebar-handle--start modifier so the painted line mirrors):
//
//   data-resizable-sidebar-edge-value="start"
//
// When another controller owns the element's width (the season drawer collapses
// it to a 40px rail), pass auto="false" so this one only supplies the drag
// mechanics and never applies a width on connect — otherwise whichever
// controller connects last wins, and the drawer renders as a wide empty column
// with the rail floating in it:
//
//   data-resizable-sidebar-auto-value="false"
//
// Markup expected inside the controller element:
//   <div data-resizable-sidebar-target="handle"></div>
//
// The handle's visual is CSS-driven (see application.css → .sidebar-handle).
// JS only handles geometry + persistence.
//
// Cooperates with sidebar_toggle_controller: if the rail is collapsed
// (icon-only mode), this controller does nothing. Re-applies width on
// next expansion via the `apply` action.

const NUDGE_STEP = 8 // px keyboard nudge
const NUDGE_BIG = 32 // px keyboard nudge with Shift

export default class extends Controller {
  static targets = ["handle"]
  static values = {
    storageKey: { type: String, default: "sidebar:width" },
    default: { type: Number, default: 340 },
    min: { type: Number, default: 240 },
    max: { type: Number, default: 520 },
    // "end" = drag the right edge (left rails). "start" = drag the left edge
    // (right drawers), which inverts the pointer delta.
    edge: { type: String, default: "end" },
    // false = another controller owns width; don't touch it on connect.
    auto: { type: Boolean, default: true },
  }

  connect() {
    this._onPointerDown = this._onPointerDown.bind(this)
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
    this._onKeyDown = this._onKeyDown.bind(this)
    this._onDblClick = this._onDblClick.bind(this)

    if (this.hasHandleTarget) {
      this.handleTarget.addEventListener("pointerdown", this._onPointerDown)
      this.handleTarget.addEventListener("dblclick", this._onDblClick)
      this.handleTarget.addEventListener("keydown", this._onKeyDown)
      this.handleTarget.setAttribute("tabindex", "0")
      this.handleTarget.setAttribute("role", "separator")
      this.handleTarget.setAttribute("aria-orientation", "vertical")
      this.handleTarget.setAttribute("aria-label", "Resize sidebar")
    }

    if (this.autoValue) this._applyWidth(this._storedWidth())
  }

  disconnect() {
    if (this.hasHandleTarget) {
      this.handleTarget.removeEventListener("pointerdown", this._onPointerDown)
      this.handleTarget.removeEventListener("dblclick", this._onDblClick)
      this.handleTarget.removeEventListener("keydown", this._onKeyDown)
    }
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    this._releaseBodyCursor()
  }

  // Re-apply the stored width — call from sidebar_toggle_controller when
  // it un-collapses the rail.
  apply() {
    this._applyWidth(this._storedWidth())
  }

  // ───── pointer ─────

  _onPointerDown(e) {
    if (e.button !== 0) return
    e.preventDefault()
    this._dragStartX = e.clientX
    this._dragStartW = this.element.getBoundingClientRect().width
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    this.element.classList.add("is-resizing")
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
  }

  _onPointerMove(e) {
    const delta = (e.clientX - this._dragStartX) * this._direction
    this._applyWidth(this._clamp(this._dragStartW + delta), { persist: false })
  }

  _onPointerUp() {
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    this._releaseBodyCursor()
    this.element.classList.remove("is-resizing")
    // Write to storage on release, not every frame
    this._persistWidth(this.element.getBoundingClientRect().width)
  }

  _onDblClick() {
    this._applyWidth(this.defaultValue)
  }

  _onKeyDown(e) {
    let delta = 0
    if (e.key === "ArrowLeft") delta = -1
    else if (e.key === "ArrowRight") delta = +1
    if (!delta) return
    e.preventDefault()
    const step = e.shiftKey ? NUDGE_BIG : NUDGE_STEP
    const cur = this.element.getBoundingClientRect().width
    this._applyWidth(this._clamp(cur + delta * step * this._direction))
  }

  // +1 when dragging right widens, -1 when dragging left widens.
  get _direction() {
    return this.edgeValue === "start" ? -1 : 1
  }

  // ───── width plumbing ─────

  _applyWidth(w, { persist = true } = {}) {
    this.element.style.width = `${w}px`
    if (persist) this._persistWidth(w)
  }

  _persistWidth(w) {
    try {
      localStorage.setItem(this.storageKeyValue, String(Math.round(w)))
    } catch (_) {
      /* private mode etc. — width is just non-sticky */
    }
  }

  _storedWidth() {
    try {
      const n = parseInt(localStorage.getItem(this.storageKeyValue), 10)
      return Number.isFinite(n) ? this._clamp(n) : this.defaultValue
    } catch (_) {
      return this.defaultValue
    }
  }

  _clamp(w) {
    return Math.min(this.maxValue, Math.max(this.minValue, w))
  }

  _releaseBodyCursor() {
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }
}
